import React, {useMemo} from 'react';
import {MultiSelect, Select} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import type {UserTypeEnum} from 'src/api/ProactApiClient';
import {DepartmentSelectBox} from 'src/app/databound-elements/DepartmentSelectBox';
import {ProjectSelectBox} from 'src/app/databound-elements/ProjectSelectBox';
import {PersonSelectBox} from 'src/app/databound-elements/PersonSelectBox';
import {useGetCourseDefinitionSelectOptionsQuery} from 'src/redux/queries/courseDefinition';
import {useGetVendorSelectOptionsQuery} from 'src/redux/queries/vendor';
import {UserTypeDataOptions} from 'src/shared/constants/columnFilterData';
import {useProjectScope} from 'src/shared/hooks/useProjectScope';
import type {ISelectOption} from 'src/types/Form';

export interface ComplianceFilters {
  courseDefinitionIds: string[];
  departmentId?: string;
  supervisorId?: string;
  vendorId?: string;
  projectId?: string;
  userTypes: UserTypeEnum[];
}

export const EMPTY_COMPLIANCE_FILTERS: ComplianceFilters = {
  courseDefinitionIds: [],
  departmentId: undefined,
  supervisorId: undefined,
  vendorId: undefined,
  projectId: undefined,
  userTypes: [],
};

export interface ComplianceFiltersBarProps {
  filters: ComplianceFilters;
  onChange: (filters: ComplianceFilters) => void;
}

/**
 * Shared filter controls (course, department, supervisor, vendor, project,
 * user type) driving both the aggregate stat cards/pivots and the
 * assignments grid — every filter here is supported server-side by
 * `GetCourseAssignmentListRequestV2` / `GetCourseAssignmentAggregateRequest`.
 */
export const ComplianceFiltersBar: React.FC<ComplianceFiltersBarProps> = ({
  filters,
  onChange,
}) => {
  // The sidebar project scope already narrows every request, so an in-page
  // project filter would be redundant and could contradict it.
  const projectScope = useProjectScope();

  const {data: courseOptions} = useGetCourseDefinitionSelectOptionsQuery({
    start: 0,
    length: 500,
    sortColumn: 'name',
    sortDirection: 'asc',
  });
  const {data: vendorOptions} = useGetVendorSelectOptionsQuery({
    start: 0,
    length: 500,
    active: true,
  });

  const courseData = useMemo(
    () =>
      (courseOptions?.data ?? []).map(option => ({
        value: String(option.id),
        label: option.label ?? String(option.id),
      })),
    [courseOptions?.data]
  );
  const vendorData = useMemo(
    () =>
      (vendorOptions?.data ?? []).map(option => ({
        value: String(option.id),
        label: option.label ?? String(option.id),
      })),
    [vendorOptions?.data]
  );

  return (
    <Group gap="sm" mb="md" align="flex-end" wrap="wrap">
      <Box w={{base: '100%', sm: 260}}>
        <MultiSelect
          label="Course"
          placeholder="All courses"
          data={courseData}
          value={filters.courseDefinitionIds}
          onChange={value => onChange({...filters, courseDefinitionIds: value})}
          searchable
          clearable
        />
      </Box>
      <Box w={{base: '100%', sm: 220}}>
        <DepartmentSelectBox
          label="Department"
          queryOptions={{}}
          value={filters.departmentId}
          placeholder="All departments"
          onSelectChangeCallback={(value: ISelectOption | null) =>
            onChange({
              ...filters,
              departmentId: value?.id ? String(value.id) : undefined,
            })
          }
        />
      </Box>
      <Box w={{base: '100%', sm: 220}}>
        <PersonSelectBox
          label="Supervisor"
          queryOptions={{superviserOnly: true}}
          value={filters.supervisorId}
          placeholder="All supervisors"
          onSelectChangeCallback={(value: ISelectOption | null) =>
            onChange({
              ...filters,
              supervisorId: value?.id ? String(value.id) : undefined,
            })
          }
        />
      </Box>
      <Box w={{base: '100%', sm: 220}}>
        <Select
          label="Vendor"
          placeholder="All vendors"
          data={vendorData}
          value={filters.vendorId ?? null}
          onChange={value =>
            onChange({...filters, vendorId: value ?? undefined})
          }
          searchable
          clearable
        />
      </Box>
      {projectScope === null && (
        <Box w={{base: '100%', sm: 220}}>
          <ProjectSelectBox
            label="Project"
            queryOptions={{}}
            value={filters.projectId ?? ''}
            includeInstanceWide
            onSelectChangeCallback={(value: ISelectOption | null) =>
              onChange({
                ...filters,
                projectId: value?.id ? String(value.id) : undefined,
              })
            }
          />
        </Box>
      )}
      <Box w={{base: '100%', sm: 220}}>
        <MultiSelect
          label="User type"
          placeholder="All user types"
          data={UserTypeDataOptions}
          value={filters.userTypes.map(String)}
          onChange={value =>
            onChange({
              ...filters,
              userTypes: value.map(v => Number(v) as UserTypeEnum),
            })
          }
          clearable
        />
      </Box>
    </Group>
  );
};
